import React, { useState, useEffect } from 'react';
import { listDriveFiles, listDriveFolders } from '../services/googleDriveClient';
import {
    FolderIcon,
    ChevronRightIcon,
    ArrowLeftIcon,
    VideoIcon,
    CheckIcon,
    XMarkIcon
} from './icons';

interface DriveFile {
    id: string;
    name: string;
    mimeType: string;
    thumbnailLink?: string;
    webViewLink?: string;
    parents?: string[];
}

interface DriveFolder {
    id: string;
    name: string;
    mimeType?: string;
}

interface DriveFilePickerProps {
    onSelect: (file: DriveFile) => void;
    onCancel: () => void;
    mimeTypeFilter?: string; // e.g. 'video/*'
}

export const DriveFilePicker: React.FC<DriveFilePickerProps> = ({
    onSelect,
    onCancel,
    mimeTypeFilter = 'video/*'
}) => {
    const [currentPath, setCurrentPath] = useState<DriveFolder[]>([{ id: 'root', name: 'My Drive' }]);
    const [items, setItems] = useState<(DriveFile | DriveFolder)[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState<DriveFile | null>(null);

    const currentFolder = currentPath[currentPath.length - 1];

    useEffect(() => {
        loadContent(currentFolder.id === 'root' ? undefined : currentFolder.id);
    }, [currentFolder, mimeTypeFilter]);

    const loadContent = async (parentId?: string) => {
        setIsLoading(true);
        try {
            // Load folders and files in parallel
            const [foldersData, filesData] = await Promise.all([
                listDriveFolders(parentId),
                listDriveFiles(mimeTypeFilter, parentId)
            ]);

            // Combine and sort: folders first, then files
            const combined = [
                ...foldersData.map(f => ({ ...f, mimeType: 'application/vnd.google-apps.folder' })),
                ...filesData
            ];
            setItems(combined);
        } catch (error) {
            console.error('Failed to load Drive content:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleNavigate = (folder: DriveFolder) => {
        setCurrentPath([...currentPath, folder]);
        setSelectedFile(null); // Deselect when changing folder
    };

    const handleNavigateUp = () => {
        if (currentPath.length > 1) {
            setCurrentPath(currentPath.slice(0, -1));
            setSelectedFile(null);
        }
    };

    const handleItemClick = (item: DriveFile | DriveFolder) => {
        if (item.mimeType === 'application/vnd.google-apps.folder') {
            handleNavigate(item as DriveFolder);
        } else {
            setSelectedFile(item as DriveFile);
        }
    };

    return (
        <div className="bg-[#1f1f1f] border border-gray-700 rounded-xl overflow-hidden flex flex-col h-[600px] w-full max-w-3xl mx-auto shadow-2xl relative">
            {/* Header */}
            <div className="p-4 border-b border-gray-700 bg-[#252526] flex items-center justify-between">
                <div className="flex items-center gap-3">
                    {currentPath.length > 1 && (
                        <button
                            onClick={handleNavigateUp}
                            className="p-1 hover:bg-gray-700 rounded-full transition-colors"
                        >
                            <ArrowLeftIcon className="w-5 h-5 text-gray-400" />
                        </button>
                    )}
                    <h3 className="font-bold text-white text-lg flex items-center gap-2">
                        <img src="https://upload.wikimedia.org/wikipedia/commons/1/12/Google_Drive_icon_%282020%29.svg" className="w-5 h-5" alt="Drive" />
                        {currentFolder.name}
                    </h3>
                </div>
                <button
                    onClick={onCancel}
                    className="p-1 hover:bg-gray-700 rounded-full transition-colors"
                >
                    <XMarkIcon className="w-5 h-5 text-gray-400" />
                </button>
            </div>

            {/* Content List */}
            <div className="flex-1 overflow-y-auto p-4 bg-[#18181b]">
                {isLoading ? (
                    <div className="flex justify-center p-8">
                        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                ) : items.length === 0 ? (
                    <div className="text-center text-gray-500 py-10 flex flex-col items-center gap-2">
                        <FolderIcon className="w-10 h-10 text-gray-700" />
                        <span>Dossier vide</span>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {items.map((item: any) => {
                            const isFolder = item.mimeType === 'application/vnd.google-apps.folder';
                            const isSelected = selectedFile?.id === item.id;

                            return (
                                <div
                                    key={item.id}
                                    onClick={() => handleItemClick(item)}
                                    className={`group flex flex-col p-2 rounded-lg border transition-all cursor-pointer h-full
                                        ${isSelected
                                            ? 'bg-blue-500/20 border-blue-500 shadow-lg shadow-blue-500/10'
                                            : 'bg-[#252526] border-gray-800 hover:border-gray-600 hover:bg-[#2a2a2bd]'
                                        }`}
                                >
                                    {/* Thumbnail / Icon */}
                                    <div className="aspect-video w-full bg-black/50 rounded flex items-center justify-center mb-2 overflow-hidden relative">
                                        {isFolder ? (
                                            <FolderIcon className="w-10 h-10 text-gray-500 group-hover:text-blue-400 transition-colors" />
                                        ) : item.thumbnailLink ? (
                                            <img src={item.thumbnailLink} alt={item.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <VideoIcon className="w-8 h-8 text-gray-500" />
                                        )}
                                        {/* Play Overlay if video */}
                                        {!isFolder && (
                                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/30 transition-opacity">
                                                <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                                                    <div className="w-0 h-0 border-t-[5px] border-t-transparent border-l-[8px] border-l-white border-b-[5px] border-b-transparent ml-1"></div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Name */}
                                    <div className="mt-auto">
                                        <div className={`text-xs font-medium truncate ${isSelected ? 'text-blue-200' : 'text-gray-300'}`}>
                                            {item.name}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-700 bg-[#252526] flex justify-between items-center glass">
                <div className="text-xs text-gray-500">
                    {selectedFile ? `Sélectionné: ${selectedFile.name}` : 'Aucune sélection'}
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 rounded-lg text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
                    >
                        Annuler
                    </button>
                    <button
                        disabled={!selectedFile}
                        onClick={() => selectedFile && onSelect(selectedFile)}
                        className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-all
                            ${selectedFile
                                ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/20'
                                : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                            }`}
                    >
                        <CheckIcon className="w-4 h-4" />
                        Choisir
                    </button>
                </div>
            </div>
        </div>
    );
};
